import { api } from "api";
import { Utils } from "utils";
import { Hotels } from "services";
import { ThunkType } from "store/types";
import { DurationKey } from "constants/enums";
import { setRouteAuthMember } from "store/route-auth";

import {
  addRoutes,
  setRoutes,
  setIsLoading,
  setTotalCount,
  setIsAuthModalVisible,
} from "./slice";
import { PostMessageKeys } from "constants/constants";

const DurationValue = Object.freeze({
  [DurationKey["1-2 days"]]: { min: 1, max: 2 },
  [DurationKey["3-5 days"]]: { min: 3, max: 5 },
  [DurationKey["6-15 days"]]: { min: 6, max: 15 },
  [DurationKey["16-30 days"]]: { min: 16, max: 30 },
});

export const getRoutesThunk =
  (): ThunkType<Promise<void>> => async (dispatch, getStore) => {
    try {
      dispatch(setIsLoading(true));
      const filters = getStore().routesList.filters;

      const response = await api.route.usersRoutes({
        roadType: filters.roadType ?? undefined,
        countryCode: filters.country || undefined,
        duration: filters.duration
          ? DurationValue[filters.duration as DurationKey]
          : undefined,
      });

      const { totalCount, list } = response;

      dispatch(setRoutes(list));
      dispatch(setTotalCount(totalCount));

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const getMoreRoutesThunk =
  (): ThunkType<Promise<void>> => async (dispatch, getStore) => {
    try {
      dispatch(setIsLoading(true));
      const { filters, list } = getStore().routesList;
      const lastItemCreatedAt = list[list.length - 1].createdAt;

      const response = await api.route.usersRoutes({
        // @ts-ignore
        afterCreatedAt: lastItemCreatedAt._seconds,
        roadType: filters.roadType ?? undefined,
        countryCode: filters.country || undefined,
        duration: filters.duration
          ? DurationValue[filters.duration as DurationKey]
          : undefined,
      });

      const { totalCount, list: nextList } = response;

      dispatch(addRoutes(nextList));
      dispatch(setTotalCount(totalCount));

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const checkAuthStatusThunk =
  (callback: () => void): ThunkType<void> =>
  (dispatch, getStore) => {
    const isAuthed = getStore().routeAuthState.data.isAuthed;

    if (isAuthed) {
      callback();
      return;
    }

    // Show "Full Access" Dialog
    // + send .postMessage to iframe parrent to scroll to top;

    dispatch(setIsAuthModalVisible(true));

    Utils.IFrame.postMessage({ key: PostMessageKeys.RoutesList.ScrollToTop });

    // window.parent.postMessage(
    //   { task: "task_scroll_to_top" },
    //   { targetOrigin: "*" }
    // );
  };

export const getHotelsThunk =
  (requestID: string, dayIndex: number): ThunkType<Promise<void>> =>
  async (dispatch, getStore) => {
    try {
      const store = getStore();
      const routes = store.routesList.list;
      // const data = selectRouteResponse(requestID)(store);

      let updatedRoutes: typeof routes = [];
      const requestedRoute = routes.find((item) => item.id === requestID);

      if (
        requestedRoute &&
        requestedRoute.response &&
        requestedRoute.response.length > 0
      ) {
        const { selectedPlaces = [] } = requestedRoute.response[dayIndex];
        const lastPlace = selectedPlaces[selectedPlaces.length - 1];

        if (lastPlace) {
          const countryCode = lastPlace.address_info.country_code;
          const name = lastPlace.address_info.city ?? lastPlace.title;
          const countryName = Utils.Helpers.getCountryName(countryCode);

          const hotels = new Hotels(countryName, name);
          await hotels.search();
          const list = hotels.list;

          if (list.length > 0) {
            updatedRoutes = routes.map((route) => {
              if (route.id === requestID) {
                return {
                  ...route,
                  response: route.response!.map((resp, index) =>
                    index === dayIndex ? { ...resp, hotels: list } : resp
                  ),
                };
              }

              return route;
            });
          }
        }
      }

      if (updatedRoutes.length > 0) {
        dispatch(setRoutes(updatedRoutes));
      }

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const rateRouteThunk =
  (props: { requestID: string; rating: number }): ThunkType<Promise<void>> =>
  async (dispatch, getStore) => {
    try {
      const { requestID, rating } = props;

      const store = getStore();
      const currentMember = store.routeAuthState.data.member;

      if (currentMember) {
        await api.route.rate({ requestID, rating, email: currentMember.email });

        const prevRatings = currentMember.routeRatings ?? {};

        dispatch(
          setRouteAuthMember({
            ...currentMember,
            routeRatings: { ...prevRatings, [requestID]: rating },
          })
        );
      }

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  };
